<template>
	<div>
		<!-- Customer Select -->
		<AutoCompleteInput
			v-model="customer.id"
			hide-details
			:items="items"
			:id="inputId"
			:hide-top-margin="hideTopMargin"
			:loading="pageLoading"
			:disabled="disabled"
			:placeholder="placeholder"
			v-on:change="fetchCustomer($event)"
			:rules="rules"
			@blur="$emit('blur', $event)"
			@change="$emit('change1', customer.id)"
			:class="{ ...contentClass }"
		>
		</AutoCompleteInput>

		<div class="d-flex flex-wrap justify-space-between">
			<!-- Company Detail -->
			<div class="pa-3 col-md-6 col-lg-3" v-if="!is_empty(customer_detail)">
				<div class="blue--text text--darken-4 text-uppercase">
					<b>Company Detail</b>
				</div>

				<p class="mb-0" v-if="customer_detail.company_name">
					<v-icon size="16" class="mr-1">mdi-home-city-outline</v-icon>
					<ShowValue
						tooltip
						tooltip-text="Company name"
						:object="customer_detail"
						object-key="company_name"
						label="company name"
					></ShowValue>
				</p>
				<p class="mb-0" v-if="customer_detail.display_name">
					<v-icon size="16" class="mr-1">mdi-account</v-icon>
					<ShowValue
						tooltip
						tooltip-text="display name"
						:object="customer_detail"
						object-key="display_name"
						label="display name"
					></ShowValue>
				</p>
				<p class="mb-0" v-if="customer_detail.phone">
					<v-icon size="16" class="mr-1">mdi-phone</v-icon>
					<ShowValue
						tooltip
						click-to-copy
						tooltip-text="phone number"
						:object="customer_detail"
						object-key="phone"
						label="phone number"
					></ShowValue>
				</p>
				<p class="mb-0 text-truncate" v-if="customer_detail.email">
					<v-icon size="16" class="mr-1">mdi-email</v-icon>
					<ShowValue
						tooltip
						click-to-copy
						tooltip-text="email"
						:object="customer_detail"
						object-key="email"
						label="email"
					></ShowValue>
				</p>
			</div>

			<!-- Contact Person -->
			<div class="pa-3 col-md-6 col-lg-3" v-if="!is_empty(contact_person)">
				<div class="blue--text text--darken-4 text-uppercase d-flex align-center">
					<b>Person Incharge</b>

					<v-menu offset-y max-width="300">
						<template v-slot:activator="{ on, attrs }">
							<v-icon
								:disabled="pageLoading"
								v-bind="attrs"
								v-on="on"
								size="18"
								color="blue darken-4"
								class="ml-3 cursor-pointer"
								>mdi-pencil-circle</v-icon
							>
						</template>

						<v-list dense style="padding: 8px !important">
							<v-list-item-group>
								<v-list-item
									v-for="item in contact_person_list"
									:key="item.id"
									@click="handleContactPerson(item.id)"
									:class="{ 'blue lighten-4': item.id === contact_person.id }"
								>
									<v-list-item-icon class="mr-2 position-relative">
										<v-icon :color="item.primary ? 'green' : ''">mdi-account-circle</v-icon>
										<v-icon v-if="item.primary" size="10" color="green" class="ml-1 billing-active"
											>mdi-circle</v-icon
										>
									</v-list-item-icon>
									<v-list-item-title class="text-truncate"
										>{{ item.barcode }}-{{ item.display_name }}</v-list-item-title
									>
								</v-list-item>
							</v-list-item-group>
						</v-list>
					</v-menu>
				</div>

				<p class="mb-0" v-if="contact_person.display_name">
					<v-icon size="16" class="mr-1">mdi-account</v-icon>{{ contact_person.display_name }}
				</p>
				<p class="mb-0" v-if="contact_person.phone">
					<v-icon size="16" class="mr-1">mdi-phone</v-icon>{{ contact_person.phone }}
				</p>
				<p class="mb-0 text-truncate" v-if="contact_person.email">
					<v-icon size="16" class="mr-1">mdi-email</v-icon>
					{{ contact_person.email }}
				</p>
			</div>

			<!-- Billing Address -->
			<div
				class="pa-3 col-md-6 col-lg-3"
				v-if="!is_empty(default_billing_address) && module != 'quotation'"
			>
				<div class="d-flex justify-space-between">
					<div>
						<div class="blue--text text--darken-4 text-uppercase no-wrap">
							<b>Billing Address</b>
						</div>
						<p class="mb-0" v-if="default_billing_address.display_name">
							<v-icon size="16" class="mr-1">mdi-account</v-icon>{{ default_billing_address.display_name }}
						</p>
						<p class="mb-0" v-if="default_billing_address.phone_no">
							<v-icon size="16" class="mr-1">mdi-phone</v-icon>{{ default_billing_address.phone_no }}
						</p>
						<p class="mb-0 text-truncate" v-if="default_billing_address.email">
							<v-icon size="16" class="mr-1">mdi-email</v-icon>{{ default_billing_address.email }}
						</p>
						<div
							class="d-flex"
							v-if="default_billing_address.address_line_1 || default_billing_address.address_line_2"
						>
							<div>
								<v-icon size="16" class="mr-1">mdi-map</v-icon>
							</div>
							<div>
								<p class="mb-0" v-if="default_billing_address.address_line_1">
									{{ default_billing_address.address_line_1 }}
								</p>
								<p class="mb-0" v-if="default_billing_address.address_line_2">
									{{ default_billing_address.address_line_2 }}
								</p>
								<p class="mb-0" v-if="default_billing_address.unit_number">
									{{ default_billing_address.unit_number }}
								</p>
								<p class="mb-0" v-if="default_billing_address.address_country">
									{{ default_billing_address.address_country }}
								</p>
								<p class="mb-0" v-if="default_billing_address.address_postal_code">
									{{ default_billing_address.address_postal_code }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Service Address -->
			<div class="pa-3 col-md-6 col-lg-3" v-if="serviceAddress && !is_empty(billing_address)">
				<div class="d-flex justify-space-between">
					<div>
						<div class="blue--text text--darken-4 text-uppercase no-wrap">
							<b>Service Address</b>
							<v-icon v-if="false && billing_address.default" size="10" color="green" class="ml-1"
								>mdi-circle</v-icon
							>
							<v-menu
								offset-y
								max-width="300"
								@input="
									(val) => {
										if (val) searchServiceAddress = '';
									}
								"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-icon
										:disabled="pageLoading"
										v-bind="attrs"
										v-on="on"
										size="18"
										color="blue darken-4"
										class="ml-3 cursor-pointer"
										>mdi-pencil-circle</v-icon
									>
								</template>
								<v-list dense style="padding: 8px !important">
									<v-text-field
										v-model="searchServiceAddress"
										dense
										placeholder="Search address..."
										clearable
										class="mb-2"
										@click.stop
										@keydown.stop
									></v-text-field>

									<v-list-item-group>
										<v-list-item
											v-for="item in filteredBillingAddresses"
											:key="item.id"
											@click="handleBillingAddress(item.id)"
											:class="{ 'blue lighten-4': item.id === billing_address.id }"
										>
											<v-list-item-icon class="mr-2 position-relative">
												<v-icon>mdi-map-marker</v-icon>
											</v-list-item-icon>

											<v-list-item-title class="text-truncate">
												{{ item.barcode }} - {{ item.address_line_1 }}
											</v-list-item-title>
										</v-list-item>
									</v-list-item-group>
								</v-list>
							</v-menu>
						</div>
						<p class="mb-0" v-if="billing_address.display_name">
							<v-icon size="16" class="mr-1">mdi-account</v-icon>{{ billing_address.display_name }}
						</p>
						<p class="mb-0" v-if="billing_address.phone_no">
							<v-icon size="16" class="mr-1">mdi-phone</v-icon>{{ billing_address.phone_no }}
						</p>
						<p class="mb-0 text-truncate" v-if="billing_address.email">
							<v-icon size="16" class="mr-1">mdi-email</v-icon>{{ billing_address.email }}
						</p>
						<div class="d-flex" v-if="billing_address.address_line_1 || billing_address.address_line_2">
							<div>
								<v-icon size="16" class="mr-1">mdi-map</v-icon>
							</div>
							<div>
								<p class="mb-0" v-if="billing_address.address_line_1">
									{{ billing_address.address_line_1 }}
								</p>
								<p class="mb-0" v-if="billing_address.address_line_2">
									{{ billing_address.address_line_2 }}
								</p>
								<p class="mb-0" v-if="billing_address.unit_number">
									{{ billing_address.unit_number }}
								</p>
								<p class="mb-0" v-if="billing_address.address_country">
									{{ billing_address.address_country }}
								</p>
								<p class="mb-0" v-if="billing_address.address_postal_code">
									{{ billing_address.address_postal_code }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import AutoCompleteInput from "@/view/components/AutoCompleteInput";
import { GetCustomer } from "@/core/lib/quotation.lib";
import ShowValue from "@/view/components/ShowValue";
import { isEmpty } from "lodash";

export default {
	name: "SelectCustomerSupplier",

	components: {
		AutoCompleteInput,
		ShowValue,
	},
	model: {
		prop: "value",
		event: "change",
	},

	props: {
		value: { type: Object, default: () => ({}) },
		items: { type: Array, default: () => [] },
		loading: Boolean,
		serviceAddress: { type: Boolean, default: true },
		disabled: Boolean,
		inputId: String,
		module: String,
		placeholder: String,
		rules: { type: Array, default: () => [] },
		contentClass: Object,
		hideTopMargin: {
			type: Boolean,
			default: false,
		},
		updateDetail: {
			type: Object,
			default: () => {
				return {};
			},
		},
	},

	data() {
		return {
			searchServiceAddress: null,
			pageLoading: false,

			customer: {
				id: null,
				biiling_address_id: null,
				contact_person_id: null,
			},

			customer_detail: {},
			billing_address_list: [],
			billing_address: {},
			default_billing_address: {},

			contact_person_list: [],
			contact_person: {},
		};
	},

	watch: {
		updateDetail: {
			deep: true,
			immediate: true,
			handler(params) {
				console.log("Params updated:", params);
				this.customer = { ...params };
				this.fetchCustomer(
					this.customer.id,
					this.customer.billing_address_id,
					this.customer.contact_person_id
				);
			},
		},
	},

	methods: {
		async fetchCustomer(id, billingId = null, contactId = null) {
			if (!id) return this.reset();

			try {
				this.pageLoading = true;

				const { current_contact, billing_addresses, contact_persons } = await GetCustomer({
					contact: id,
				});

				this.customer_detail = current_contact;
				this.billing_address_list = billing_addresses;
				this.contact_person_list = contact_persons;

				// Default billing
				this.default_billing_address = billing_addresses.find((b) => b.default) || {};

				// Selected billing
				this.billing_address =
					billing_addresses.find((b) => b.id == billingId) ||
					billing_addresses.find((b) => !b.default) ||
					billing_addresses[0] ||
					{};

				// Selected contact person
				this.contact_person =
					contact_persons.find((c) => c.id == contactId) ||
					contact_persons.find((c) => c.primary) ||
					contact_persons[0] ||
					{};

				this.emitChange();
			} catch (error) {
				console.error("Customer fetch error:", error);
				this.reset();
			} finally {
				this.pageLoading = false;
			}
		},

		reset() {
			this.customer_detail = {};
			this.billing_address = {};
			this.default_billing_address = {};
			this.contact_person = {};
			this.emitChange();
		},

		handleBillingAddress(id) {
			this.billing_address = this.billing_address_list.find((b) => b.id === id) || {};
			this.emitChange();
		},

		handleContactPerson(id) {
			this.contact_person = this.contact_person_list.find((c) => c.id === id) || {};
			this.emitChange();
		},

		emitChange() {
			this.$emit("change", {
				id: this.customer_detail?.id || null,
				billing_address_id: this.billing_address?.id || null,
				contact_person_id: this.contact_person?.id || null,
				property_id: this.default_billing_address?.id || null,
			});
		},

		is_empty(obj) {
			return isEmpty(obj);
		},
	},
	mounted() {
		if (this.value?.id) {
			this.customer.id = this.value.id;
			this.fetchCustomer(this.value.id, this.value.billing_address_id, this.value.contact_person_id);
		}
	},
	computed: {
		filteredBillingAddresses() {
			let list = this.billing_address_list;

			// Filter by contact person
			if (this.contact_person?.id) {
				list = list.filter((item) => item.contact_person == this.contact_person.id);
			}

			// Search filter
			if (this.searchServiceAddress) {
				const search = this.searchServiceAddress.toLowerCase();

				list = list.filter(
					(item) =>
						(item.address_line_1 || "").toLowerCase().includes(search) ||
						(item.barcode || "").toLowerCase().includes(search)
				);
			}

			return list;
		},
	},
};
</script>

<style scoped>
.text-truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
